// components/AgentViewTiles.js
import React, { useState, useEffect } from "react";
import {
  FaPhone,
  FaSmile,
  FaRegCheckCircle,
  FaArrowUp,
  FaArrowDown,
  FaClock,
  FaInfoCircle,
  FaClipboardCheck,
} from "react-icons/fa";
import Tippy from "@tippyjs/react";
import "tippy.js/dist/tippy.css";
import "tippy.js/animations/shift-away.css";

function AgentViewTiles({ agent, dateRange = "today" }) {
  const [tiles, setTiles] = useState([]);
  const qualityManagementEnabled = window.appSettings?.qualityManagementEnabled;

  const getPeerComparisonElement = (agentValue, peerValue, lowerIsBetter = false) => {
    // Calculate true percentage difference from peer value
    const percentDiff = ((agentValue - peerValue) / peerValue * 100).toFixed(1);
    
    // For metrics where lower is better (like call duration), we invert what's considered positive
    const isPositive = lowerIsBetter 
      ? percentDiff < 0  // Lower is better so negative percentDiff is good
      : percentDiff > 0; // Higher is better so positive percentDiff is good
    
    // Choose arrow direction based on metric type
    const Icon = lowerIsBetter
      ? (isPositive ? FaArrowDown : FaArrowUp)  // For "lower is better" metrics: down is good
      : (isPositive ? FaArrowUp : FaArrowDown); // For other metrics: up is good
    
    const colorClass = isPositive ? "text-green-400" : "text-red-300";
    
    // Always show as a positive number with the arrow indicating direction
    const element = (
      <span className={`text-md flex items-center ${colorClass}`}>
        <Icon className="mr-1" />
        {Math.abs(percentDiff)}%
      </span>
    );
    
    return { element, isPositive, percentDiff: Math.abs(percentDiff) };
  };

  const formatTooltipContent = (tile, agentName) => {
    const { title, value, peerValue, isCallDuration } = tile;
    const { isPositive, percentDiff } = getPeerComparisonElement(
      value, 
      peerValue, 
      isCallDuration
    );
    
    const comparisonText = isPositive 
      ? `${percentDiff}% better than peer average` 
      : `${percentDiff}% below peer average`;

    const comparisonMessage = isCallDuration
      ? (isPositive ? "faster than" : "slower than")
      : (isPositive ? "better than" : "below");
    
    return (
      <div className="p-2 max-w-xs">
        <div className="font-bold mb-2 text-lg border-b pb-1">{title}</div>
        <div className="mb-2">
          <span className="font-medium text-gray-400">Agent:</span> {agentName}
        </div>
        <div className="mb-2">
          <span className="font-medium text-gray-400">Agent Value:</span> {typeof value === 'number' && !isCallDuration ? value.toFixed(3) : value}
          {isCallDuration ? "s" : ""}
        </div>
        <div className="mb-2">
          <span className="font-medium text-gray-400">Peer Average:</span> {typeof peerValue === 'number' && !isCallDuration ? peerValue.toFixed(3) : peerValue}
          {isCallDuration ? "s" : ""}
        </div>
        <div className={isPositive ? "text-green-600" : "text-red-500"}>
          This agent is {comparisonMessage} peer average ({comparisonText})
        </div>
      </div>
    );
  };

  // Function to format the calls tooltip message based on dateRange
  const formatCallsTooltipContent = () => {
    let timeFrame;
    
    // Determine time frame description
    if (typeof dateRange === 'object' && dateRange.custom) {
      const start = new Date(dateRange.startDate).toLocaleDateString();
      const end = new Date(dateRange.endDate).toLocaleDateString();
      timeFrame = `from ${start} to ${end}`;
    } else {
      // Handle standard date ranges
      switch(dateRange) {
        case 'today': timeFrame = 'today'; break;
        case 'week': timeFrame = 'this week'; break;
        case 'month': timeFrame = 'this month'; break;
        case 'year': timeFrame = 'this year'; break;
        default: timeFrame = 'in the selected period';
      }
    }

    return (
      <div className="p-2 max-w-xs">
        <div className="font-bold mb-2 text-lg border-b pb-1">Call Volume</div>
        <div className="text-center mt-1">
          Agent {agent.agent} had <span className="font-bold text-white">{agent.totalCalls || 0}</span> call{agent.totalCalls !== 1 ? 's' : ''} {timeFrame}.
        </div>
      </div>
    );
  };

  const formatQMTooltipContent = () => {
    // Extract values
    const qmScore = agent.averageQMScore || 0;
    const autofailRate = agent.averageQMAutofailRate || 0;
    const peerQMScore = agent.peerAverages?.averageQMScore || 0;
    const peerAutofailRate = agent.peerAverages?.averageQMAutofailRate || 0;
    
    // Calculate comparisons upfront
    const qmComparison = getPeerComparisonElement(qmScore, peerQMScore);
    const autofailComparison = getPeerComparisonElement(autofailRate, peerAutofailRate, true);
    
    // Calculate comparison texts
    const qmComparisonText = qmComparison.isPositive 
      ? `${qmComparison.percentDiff}% better than peer average` 
      : `${qmComparison.percentDiff}% below peer average`;
      
    const autofailComparisonText = autofailComparison.isPositive 
      ? `${autofailComparison.percentDiff}% better than peer average` 
      : `${autofailComparison.percentDiff}% below peer average`;
    
    const qmComparisonMessage = qmComparison.isPositive ? "better than" : "below";
    const autofailComparisonMessage = autofailComparison.isPositive ? "better than" : "below";
    
    return (
      <div className="p-2 max-w-sm">
        <div className="font-bold mb-2 text-lg border-b pb-1">Quality Management</div>
        <div className="mb-2">
          <span className="font-medium text-gray-400">Agent:</span> {agent.agent}
        </div>
        
        {/* Two columns side by side */}
        <div className="flex gap-4 mb-3">
          <div className="flex-1">
            <div className="mb-2">
              <span className="font-medium text-gray-400">Avg QM Score:</span> {qmScore.toFixed(2)}
            </div>
            <div className="mb-2">
              <span className="font-medium text-gray-400">Peer Average:</span> {peerQMScore.toFixed(2)}
            </div>
          </div>
          
          <div className="flex-1">
            <div className="mb-2">
              <span className="font-medium text-gray-400">Autofail Rate:</span> {(autofailRate * 100).toFixed(1)}%
            </div>
            <div className="mb-2">
              <span className="font-medium text-gray-400">Peer Average:</span> {(peerAutofailRate * 100).toFixed(1)}%
            </div>
          </div>
        </div>
        
        <div className={qmComparison.isPositive ? "text-green-600 mb-1" : "text-red-500 mb-1"}>
          This agent's average QM Score is {qmComparisonMessage} peer average ({qmComparisonText})
        </div>
        <div className={autofailComparison.isPositive ? "text-green-600" : "text-red-500"}>
          This agent's Autofail Rate is {autofailComparisonMessage} peer average ({autofailComparisonText})
        </div>
      </div>
    );
  };

  useEffect(() => {
    const baseTiles = [
      { 
        Icon: FaPhone,
        title: "Number of Calls",
        value: agent.totalCalls || 0,
        hasCustomTooltip: true  // Flag to indicate this tile has a custom tooltip
      },
      {
        Icon: FaRegCheckCircle,
        title: "Professionalism Score",
        value: agent.averageProfessionalism || 0,
        peerValue: agent.peerAverages.averageProfessionalism,
        comparison: getPeerComparisonElement(
          agent.averageProfessionalism,
          agent.peerAverages.averageProfessionalism
        ),
      },
      {
        Icon: FaSmile,
        title: "Sentiment Score",
        value: agent.averageSentiment || 0,
        peerValue: agent.peerAverages.averageSentiment,
        comparison: getPeerComparisonElement(
          agent.averageSentiment,
          agent.peerAverages.averageSentiment
        ),
      },
      {
        Icon: FaClock,
        title: "Average Handle Time",
        value: agent.averageCallDuration || 0,
        peerValue: agent.peerAverages.averageCallDuration,
        comparison: getPeerComparisonElement(
          agent.averageCallDuration,
          agent.peerAverages.averageCallDuration,
          true // Lower is better for call duration
        ),
        isCallDuration: true
      },
    ];

    // Add QM tile only if QM is enabled globally
    if (qualityManagementEnabled) {
      baseTiles.push({
        Icon: FaClipboardCheck,
        title: "Quality Management",
        value: {
          qmScore: agent.averageQMScore || 0,
          autofailRate: agent.averageQMAutofailRate || 0
        },
        isQMTile: true,
        hasQMTooltip: true,
        peerValues: {
          qmScore: agent.peerAverages?.averageQMScore,
          autofailRate: agent.peerAverages?.averageQMAutofailRate
        },
        comparisons: {
          qmScore: agent.peerAverages?.averageQMScore ? getPeerComparisonElement(
            agent.averageQMScore,
            agent.peerAverages.averageQMScore
          ) : null,
          autofailRate: agent.peerAverages?.averageQMAutofailRate ? getPeerComparisonElement(
            agent.averageQMAutofailRate,
            agent.peerAverages.averageQMAutofailRate,
            true // Lower autofail rate is better
          ) : null
        }
      });
    }

    setTiles(baseTiles);
  }, [agent, dateRange, qualityManagementEnabled]);

  return (
    <div className={`grid grid-cols-1 md:grid-cols-3 xl:grid-cols-4 ${qualityManagementEnabled ? 'xl:grid-cols-5' : ''} gap-4 lg:gap-6 mb-8 mx-auto container`}>
      {tiles.map((tile, index) => {
        const tileContent = (
          <div
            key={index}
            className="usan-primary text-white p-5 rounded-xl shadow-lg flex flex-col justify-between h-27 transition-all duration-300 hover:shadow-xl hover:scale-102"
          >
            <div className="flex justify-between items-start">
              <h3 className="text-md font-semibold uppercase tracking-wide">
                {tile.title}
              </h3>
              <tile.Icon className="text-2xl opacity-80" />
            </div>
            
            {tile.title === "Number of Calls" ? (
              <div className="flex items-center justify-center h-full mt-2">
                <p className="text-4xl font-bold">{tile.value}</p>
              </div>
            ) : tile.isQMTile ? (
              // QM tile with two metrics side by side
              <div className="flex items-center justify-center mt-2 gap-4">
                <div className="text-center">
                  <p className="text-2xl font-bold">
                    {typeof tile.value.qmScore === 'number' ? tile.value.qmScore.toFixed(1) : 'N/A'}
                  </p>
                  <p className="text-xs uppercase tracking-wide opacity-80">Avg QM Score</p>
                  {tile.comparisons?.qmScore && (
                    <div className="flex flex-col items-center mt-1">
                      <div className="flex items-center">
                        {tile.comparisons.qmScore.element}
                      </div>
                      <span className="text-xs text-gray-300">(vs peers)</span>
                    </div>
                  )}
                </div>
                
                <div className="h-8 w-px bg-white opacity-30"></div>
                
                <div className="text-center">
                  <p className="text-2xl font-bold">
                    {typeof tile.value.autofailRate === 'number' ? (tile.value.autofailRate * 100).toFixed(1) : 'N/A'}%
                  </p>
                  <p className="text-xs uppercase tracking-wide opacity-80">Autofail Rate</p>
                  {tile.comparisons?.autofailRate && (
                    <div className="flex flex-col items-center mt-1">
                      <div className="flex items-center">
                        {tile.comparisons.autofailRate.element}
                      </div>
                      <span className="text-xs text-gray-300">(vs peers)</span>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-full gap-10">
                <p className="text-4xl font-bold">
                  {typeof tile.value === 'number' 
                    ? (!tile.isCallDuration 
                        ? tile.value.toFixed(3) 
                        : tile.value) 
                    : tile.value}
                  {tile.isCallDuration && "s"}
                </p>
                
                {tile.comparison && (
                  <div className="flex items-center">
                    {tile.comparison.element}
                    {tile.peerValue && (
                      <Tippy
                        content={
                          <FaInfoCircle className="text-gray-300 ml-1 cursor-help" />
                        }
                      >
                        <span className="text-xs text-gray-300 ml-1">(vs peers)</span>
                      </Tippy>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        );

        // Determine if we need a tooltip and which content to show
        if (tile.hasCustomTooltip) {
          return (
            <Tippy
              key={index}
              content={formatCallsTooltipContent()}
              placement="bottom"
              arrow={true}
              duration={[200, 200]}
              animation="shift-away"
              interactive={true}
              className="custom-tooltip"
              appendTo={document.body}
            >
              {tileContent}
            </Tippy>
          );
        } else if (tile.hasQMTooltip) {
          return (
            <Tippy
              key={index}
              content={formatQMTooltipContent()}
              placement="bottom"
              arrow={true}
              duration={[200, 200]}
              animation="shift-away"
              interactive={true}
              className="custom-tooltip"
              appendTo={document.body}
            >
              {tileContent}
            </Tippy>
          );
        } else if (tile.peerValue) {
          return (
            <Tippy
              key={index}
              content={formatTooltipContent(tile, agent.agent)}
              placement="bottom"
              arrow={true}
              duration={[200, 200]}
              animation="shift-away"
              interactive={true}
              className="custom-tooltip"
              appendTo={document.body}
            >
              {tileContent}
            </Tippy>
          );
        } else {
          return <div key={index}>{tileContent}</div>;
        }
      })}
    </div>
  );
}

export default AgentViewTiles;